/**
 * Lightweight translation engine.
 *
 * No external dep — keeps the bundle small and avoids the SSR/Suspense
 * complexity of next-intl. Supports:
 *   - dotted-path keys against a typed dictionary (`pages.issues.title`)
 *   - {placeholder} interpolation
 *   - English fallback when the active locale is missing the key
 */

import { DEFAULT_LOCALE, type SupportedLocale } from "./config";
import { DICTIONARIES, type Dictionary } from "./dictionaries";

export type TranslateParams = Record<string, string | number>;

/** Resolve a dotted path through an object — returns string|undefined. */
function resolvePath(dict: unknown, path: string): string | undefined {
  const parts = path.split(".");
  let cur: any = dict;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return typeof cur === "string" ? cur : undefined;
}

function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const v = params[key];
    return v === undefined || v === null ? `{${key}}` : String(v);
  });
}

/**
 * Translate a key against a dictionary, with English fallback.
 *
 * The fallback path means a half-translated locale still renders meaningful
 * UI: missing strings appear in English instead of as a raw key. We log the
 * miss to the console once per session in dev so the gap is visible.
 */
export function translate(
  locale: SupportedLocale,
  key: string,
  params?: TranslateParams,
): string {
  const dict = DICTIONARIES[locale] ?? DICTIONARIES[DEFAULT_LOCALE];
  let value = resolvePath(dict, key);
  if (value === undefined && locale !== DEFAULT_LOCALE) {
    value = resolvePath(DICTIONARIES[DEFAULT_LOCALE], key);
    if (value !== undefined && process.env.NODE_ENV !== "production") {
      warnOnceMissing(locale, key);
    }
  }
  if (value === undefined) return key; // last-resort: surface the key
  return interpolate(value, params);
}

const warned = new Set<string>();
function warnOnceMissing(locale: string, key: string) {
  const tag = `${locale}:${key}`;
  if (warned.has(tag)) return;
  warned.add(tag);
  console.warn(`[i18n] missing translation for ${tag}, falling back to ${DEFAULT_LOCALE}`);
}

/** Bound translator — convenient for components that already know the locale. */
export type T = (key: string, params?: TranslateParams) => string;

export function makeTranslator(locale: SupportedLocale): T {
  return (key, params) => translate(locale, key, params);
}

/**
 * Format a timestamp as a localized "X minutes ago" string. Lives here rather
 * than in lib/workspace.ts so the original sync `timeAgo()` (English-only)
 * stays available for non-React callers (logs, exports, tests).
 */
export function formatTimeAgo(iso: string, t: T): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return s < 10 ? t("time.justNow") : t("time.secondsAgoShort", { n: s });
  const m = Math.floor(s / 60);
  if (m < 60) return t("time.minutesAgoShort", { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("time.hoursAgoShort", { n: h });
  const d = Math.floor(h / 24);
  return t("time.daysAgoShort", { n: d });
}

export type { Dictionary };
